import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Table } from "antd";
import { RenderClearFiltersButton, renderPaginationTotal } from "shared/ui";
import {
  MachineItem,
  TaskExecution,
  useGetTasksExecutionQuery,
  UserItem,
} from "shared/api";
import {
  useDateFilters,
  useDebouncedTableFilter,
  usePagination,
} from "shared/hooks";
import { useGetTaskExecutionsColumns } from "../../../../../shared/hooks/useGetTaskExecutionsColumns";
import { StatusEditModal } from "./StatusEditModal";
import { UserEditModal } from "./UserEditModal";
import { TaskExecutionFilesModal } from "./TaskExecutionFilesModal";

interface Props {
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  allUsers: UserItem[];
  machinesData: MachineItem[] | undefined;
  taskIsNull?: boolean;
  userIsNull?: boolean;
  externalStatusValues?: string[];
  /** Deep link from Overview (task column user filter — username). */
  urlInitialUserName?: string;
  /** Deep link from Overview (`machine` query matches `MachineItem.name`). */
  urlInitialMachineIds?: number[];
}

const TaskScheduleTable = forwardRef(
  (
    {
      transformedUsers,
      allUsers,
      machinesData,
      taskIsNull,
      userIsNull,
      externalStatusValues,
      urlInitialUserName,
      urlInitialMachineIds,
    }: Props,
    ref
  ) => {
    const [selectedUserName, setSelectedUserName] = useState<string | null>(
      null
    );
    const pagination = usePagination(10);
    const {
      setCurrentPage,
      currentPage,
      pageSize,
      setPageSize,
      offset,
      limit,
    } = pagination;

    const [priorityGte, setPriorityGte] = useState<number | null>(null);
    const [priorityLte, setPriorityLte] = useState<number | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedExec, setSelectedExec] = useState<TaskExecution | null>(
      null
    );
    const [selectedMachines, setSelectedMachines] = useState<number[]>([]);
    const [statusValues, setStatusValues] = useState<string[]>([]);

    useEffect(() => {
      if (urlInitialUserName) {
        setSelectedUserName(urlInitialUserName);
      }
    }, [urlInitialUserName]);

    useEffect(() => {
      if (urlInitialMachineIds?.length) {
        setSelectedMachines(urlInitialMachineIds);
      }
    }, [urlInitialMachineIds]);

    const mergedStatusValues =
      externalStatusValues !== undefined
        ? Array.from(new Set([...externalStatusValues, ...statusValues]))
        : statusValues;

    const handleStatusFilterChange: React.Dispatch<
      React.SetStateAction<string[]>
    > = (action) => {
      const mergedForPrev =
        externalStatusValues !== undefined
          ? Array.from(new Set([...externalStatusValues, ...statusValues]))
          : statusValues;

      const newVals =
        typeof action === "function" ? action(mergedForPrev) : action;

      if (externalStatusValues !== undefined) {
        setStatusValues(
          newVals.filter((v) => !externalStatusValues.includes(v))
        );
        return;
      }
      setStatusValues(newVals);
    };

    const openStatusModal = (rec: TaskExecution) => {
      setSelectedExec(rec);
      setModalOpen(true);
    };

    const [userModalOpen, setUserModalOpen] = useState(false);
    const [selectedUserRecord, setSelectedUserRecord] =
      useState<TaskExecution | null>(null);

    const openUserModal = (rec: TaskExecution) => {
      setSelectedUserRecord(rec);
      setUserModalOpen(true);
    };

    const [filesModalOpen, setFilesModalOpen] = useState(false);
    const [selectedFilesRecord, setSelectedFilesRecord] =
      useState<TaskExecution | null>(null);

    const openFilesModal = (rec: TaskExecution) => {
      setSelectedFilesRecord(rec);
      setFilesModalOpen(true);
    };

    const handlePriorityGte = (value: number | null) => {
      setPriorityGte(value);
    };
    const handlePriorityLte = (value: number | null) => {
      setPriorityLte(value);
    };
    const clearPriorityValues = () => {
      setPriorityGte(null);
      setPriorityLte(null);
    };
    const isPriorityFilterActive = priorityGte !== null || priorityLte !== null;

    const {
      debouncedValue: debouncedTaskNameValue,
      handleFilterChange: handleTaskNameValue,
      value: taskNameValue,
      clearFilterValue: clearTaskNameValue,
    } = useDebouncedTableFilter("taskExecutionName", 400);

    const {
      debouncedValue: debouncedTaskMessageValue,
      handleFilterChange: handleTaskMessageValue,
      value: taskMessageValue,
      clearFilterValue: clearTaskMessageValue,
    } = useDebouncedTableFilter("taskExecutionMessageName", 400);

    const {
      dateGreater: expectedDateGreater,
      dateLesser: expectedDateLesser,
      clearDates: clearExpectedDates,
      handleDateGreater: handleExpectedDateGreater,
      handleDateLesser: handleExpectedDateLesser,
      isDataFilterActive: isExpectedFilterActive,
    } = useDateFilters();

    const {
      dateGreater: executedDateGreater,
      dateLesser: executedDateLesser,
      clearDates: clearExecutedDates,
      handleDateGreater: handleExecutedDateGreater,
      handleDateLesser: handleExecutedDateLesser,
      isDataFilterActive: isExecutedFilterActive,
    } = useDateFilters();

    const {
      data: executionTasks,
      refetch: refetchExecutions,
      isLoading,
      isFetching,
    } = useGetTasksExecutionQuery({
      offset,
      limit,
      task__name__icontains: debouncedTaskNameValue || "",
      timestamp_expected__gte: expectedDateGreater ?? undefined,
      timestamp_expected__lte: expectedDateLesser ?? undefined,
      timestamp_executed__gte: executedDateGreater ?? undefined,
      timestamp_executed__lte: executedDateLesser ?? undefined,
      status__in: mergedStatusValues.length ? mergedStatusValues : undefined,
      message: debouncedTaskMessageValue || "",
      priority__gte: priorityGte ?? undefined,
      priority__lte: priorityLte ?? undefined,
      user__username: selectedUserName ?? undefined,
      machine__in: selectedMachines.length > 0 ? selectedMachines : undefined,
      task__isnull: taskIsNull ?? undefined,
      user__isnull: userIsNull ?? undefined,
      expand: "task,machine,user",
    });

    useImperativeHandle(ref, () => ({
      refetchExecutions,
    }));

    const taskExecutionsColumns = useGetTaskExecutionsColumns({
      transformedUsers,
      // Task Name
      handleTaskNameValue,
      taskNameValue,
      // Expected Time
      handleExpectedDateGreater,
      expectedDateGreater,
      handleExpectedDateLesser,
      expectedDateLesser,
      isExpectedFilterActive,
      // Executed Time
      handleExecutedDateGreater,
      handleExecutedDateLesser,
      executedDateGreater,
      executedDateLesser,
      isExecutedFilterActive,
      // Status: merged view for UI/API; onChange keeps only extras when toolbar locks base statuses
      statusValues: mergedStatusValues,
      setStatusValues: handleStatusFilterChange,
      // Message
      handleTaskMessageValue,
      taskMessageValue,
      // Priority
      handlePriorityGte,
      handlePriorityLte,
      priorityGte,
      priorityLte,
      isPriorityFilterActive,
      // User
      selectedUserName,
      setSelectedUserName,
      // Changes status and user
      openStatusModal,
      openUserModal,
      openFilesModal,
      // Machines
      selectedMachines,
      setSelectedMachines,
      machinesData,
    });

    const isVisibleClearFiltersButton =
      !!taskNameValue ||
      !!expectedDateGreater ||
      !!expectedDateLesser ||
      !!executedDateGreater ||
      !!executedDateLesser ||
      statusValues.length > 0 ||
      !!taskMessageValue ||
      !!priorityGte ||
      !!priorityLte ||
      !!selectedUserName ||
      selectedMachines.length > 0;

    const clearAllFilters = () => {
      clearTaskNameValue();
      clearExpectedDates();
      clearExecutedDates();
      clearTaskMessageValue();
      clearPriorityValues();
      setSelectedUserName("");
      setSelectedMachines([]);
      setStatusValues([]);
    };

    return (
      <>
        <div className="mt-4">
          {isVisibleClearFiltersButton && (
            <RenderClearFiltersButton onClick={clearAllFilters} />
          )}
          <Table
            loading={isLoading || isFetching}
            dataSource={executionTasks?.results}
            columns={taskExecutionsColumns}
            pagination={{
              pageSizeOptions: ["5", "10", "20", "50"],
              current: currentPage,
              pageSize,
              total: executionTasks?.count || 0,
              showTotal: renderPaginationTotal,
              showSizeChanger: true,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            rowKey="id"
            scroll={{ x: true }}
          />
        </div>
        <StatusEditModal
          open={modalOpen}
          record={selectedExec}
          onClose={() => setModalOpen(false)}
        />
        <UserEditModal
          open={userModalOpen}
          record={selectedUserRecord}
          users={allUsers}
          onClose={() => setUserModalOpen(false)}
        />
        <TaskExecutionFilesModal
          open={filesModalOpen}
          taskExecutionId={selectedFilesRecord?.id ?? null}
          onClose={() => setFilesModalOpen(false)}
        />
      </>
    );
  }
);

TaskScheduleTable.displayName = "TaskScheduleTable";

export default TaskScheduleTable;
